/**
 * Helper panel for users who need ETH on Base.
 * Shows multiple options: bridge from Ethereum (embedded or link), transfer from exchange, buy with card.
 */
import { useState, useEffect, useRef } from 'react';
import { getAppChainConfig, isTestMode } from '../utils/network';
import BridgeEmbedInline from './BridgeEmbedInline';

const LAYERSWAP_URL = 'https://layerswap.io/app?to=base_mainnet&asset=eth';
const COINBASE_ONRAMP_URL = 'https://www.coinbase.com/onramp';
const BASE_BRIDGE_URL = 'https://bridge.base.org/deposit';

export default function GetEthHelper({ variant = 'default', compact = false, walletAddress, ethMainnetBalance, autoOpenBridge = false, onRefreshBalance, lowBalance = false }) {
  const [showBridge, setShowBridge] = useState(false);
  const hasAutoOpened = useRef(false);

  useEffect(() => {
    if (autoOpenBridge && !hasAutoOpened.current) {
      hasAutoOpened.current = true;
      setShowBridge(true);
    }
  }, [autoOpenBridge]);
  const chainCfg = getAppChainConfig();
  const chainName = chainCfg.chainName;
  const nativeSymbol = chainCfg.nativeSymbol || 'ETH';

  const bridgeUrl = isTestMode()
    ? (process.env.REACT_APP_GET_ETH_URL_TEST || '').trim()
    : (process.env.REACT_APP_GET_ETH_URL || BASE_BRIDGE_URL).trim();

  const layerswapUrl = process.env.REACT_APP_LAYERSWAP_URL || LAYERSWAP_URL;
  const buyUrl = process.env.REACT_APP_BUY_ETH_URL || COINBASE_ONRAMP_URL;

  const linkStyle = {
    color: '#1976d2',
    textDecoration: 'underline',
    fontWeight: 500,
  };

  if (compact) {
    const hasEthOnEthereum = ethMainnetBalance != null && Number(ethMainnetBalance) > 0;
    const links = [];
    if (bridgeUrl) {
      links.push(
        <span key="bridge">
          {hasEthOnEthereum && (
            <strong style={{ color: '#1565c0' }}>You have {Number(ethMainnetBalance).toFixed(4)} {nativeSymbol} on Ethereum. </strong>
          )}
          <button
            type="button"
            onClick={() => setShowBridge(true)}
            style={{ ...linkStyle, background: 'none', border: 'none', cursor: 'pointer', padding: 0, font: 'inherit' }}
          >
            Bridge here
          </button>
          {' · '}
          <a href={bridgeUrl} target="_blank" rel="noopener noreferrer" style={linkStyle}>
            or open in new tab
          </a>
        </span>
      );
    }
    links.push(
      <a key="exchange" href={layerswapUrl} target="_blank" rel="noopener noreferrer" style={linkStyle}>
        From exchange (Coinbase, etc.)
      </a>
    );
    links.push(
      <a key="buy" href={buyUrl} target="_blank" rel="noopener noreferrer" style={linkStyle}>
        Buy with card
      </a>
    );
    return (
      <>
        <p style={{ margin: '8px 0 0 0', fontSize: '0.85rem', color: '#555' }}>
          {lowBalance ? `Balance below minimum. Get more ${nativeSymbol} on ${chainName}: ` : `Get ${nativeSymbol} on ${chainName}: `}
          {links.reduce((acc, el, i) => (i === 0 ? [el] : [...acc, ' · ', el]), [])}
        </p>
        {showBridge && (
          <BridgeEmbedInline
            recipient={walletAddress}
            onHide={() => setShowBridge(false)}
            onRefreshBalance={onRefreshBalance}
          />
        )}
      </>
    );
  }

  const hasEthOnEthereum = ethMainnetBalance != null && Number(ethMainnetBalance) > 0;

  return (
    <div
      style={{
        marginTop: 12,
        padding: 14,
        backgroundColor: '#e8f4fd',
        borderRadius: 8,
        border: '1px solid #90caf9',
      }}
    >
      <h4 style={{ margin: '0 0 10px 0', fontSize: '0.95rem', color: '#1565c0' }}>
        {lowBalance ? `Need more ${nativeSymbol}? Your balance is below the minimum.` : `Get ${nativeSymbol} on ${chainName}`}
      </h4>
      {hasEthOnEthereum && (
        <div style={{ marginBottom: 12, padding: 10, backgroundColor: '#e3f2fd', borderRadius: 6, border: '1px solid #90caf9' }}>
          <p style={{ margin: 0, fontSize: '0.9rem', color: '#1565c0', fontWeight: 500 }}>
            You have {Number(ethMainnetBalance).toFixed(4)} {nativeSymbol} on Ethereum. Bridge it to {chainName} to use it here.
          </p>
          <button
            type="button"
            onClick={() => setShowBridge(true)}
            style={{
              marginTop: 8,
              padding: '8px 16px',
              fontSize: '0.9rem',
              fontWeight: 600,
              color: '#fff',
              backgroundColor: '#1976d2',
              border: 'none',
              borderRadius: 6,
              cursor: 'pointer',
            }}
          >
            Bridge to {chainName} →
          </button>
        </div>
      )}
      <p style={{ margin: hasEthOnEthereum ? '0 0 10px 0' : '0 0 10px 0', fontSize: '0.9rem', color: '#333' }}>
        {hasEthOnEthereum ? 'Or choose another option:' : 'Choose the option that fits you:'}
      </p>
      <ul style={{ margin: 0, paddingLeft: 20, fontSize: '0.9rem', color: '#333', lineHeight: 1.8 }}>
        {bridgeUrl && (
          <li>
            <strong>Have ETH on Ethereum?</strong>{' '}
            <button
              type="button"
              onClick={() => setShowBridge(true)}
              style={{ ...linkStyle, background: 'none', border: 'none', cursor: 'pointer', padding: 0, font: 'inherit' }}
            >
              Bridge here (no need to leave) →
            </button>
            {' or '}
            <a href={bridgeUrl} target="_blank" rel="noopener noreferrer" style={linkStyle}>
              open in new tab
            </a>
          </li>
        )}
        <li>
          <strong>Have crypto on an exchange?</strong> (Coinbase, Binance, Kraken, etc.){' '}
          <a href={layerswapUrl} target="_blank" rel="noopener noreferrer" style={linkStyle}>
            Transfer to {chainName} →
          </a>
        </li>
        <li>
          <strong>No crypto yet?</strong>{' '}
          <a href={buyUrl} target="_blank" rel="noopener noreferrer" style={linkStyle}>
            Buy with card →
          </a>
        </li>
      </ul>
      {showBridge && (
        <BridgeEmbedInline
          recipient={walletAddress}
          onHide={() => setShowBridge(false)}
          onRefreshBalance={onRefreshBalance}
        />
      )}
    </div>
  );
}
